import "./AiOverview.css";
import "./AiOverviewThemeFix.css";
import "./AiOverviewCollapse.css";
import { openAIZoneExtended } from "../pages/AIZoneExtended";
import { renderAIRichText } from "./AIRichText";
import { mountCurrencyConverterData } from "./CurrencyConverter";
import { mountTodaysCurrencyData } from "./TodaysCurrency";
import { renderDictionaryCard } from "./DictionaryCard";
import type { CurrencyResult } from "./currency/CurrencyTypes";
import type { TranslationOverviewPayload } from "./AiOverviewTranslator";

export interface AiSource {
  title?: string;
  url?: string;
  domain?: string;
  snippet?: string;
}

interface Section {
  title?: string;
  content?: string;
}

interface OverviewBlock {
  title?: string;
  summary?: string;
  overview?: string;
  facts?: string[];
  sections?: Section[];
}

export interface AiOverviewData {
  overview?: OverviewBlock | string;
  facts?: string[];
  sections?: Section[];
  sources?: Array<AiSource | string>;
  grounded?: boolean;
  deterministic?: boolean;
  response_type?: string;
  currency?: CurrencyResult;
  translation?: TranslationOverviewPayload;
  dictionary?: Record<string, unknown>;
}

export function renderAiOverview(
  container: HTMLElement,
  data: AiOverviewData | null,
  options: { query?: string } = {},
) {
  if (!data || !data.overview) {
    container.innerHTML = "";
    return;
  }

  const innerData = data.overview;
  let overviewText = "";
  let overviewTitle = "";

  if (typeof innerData === "string") {
    overviewText = innerData;
  } else if (innerData && typeof innerData === "object") {
    overviewTitle = innerData.title || "";
    overviewText = innerData.overview || innerData.summary || "";
  }

  const isTranslation = data.response_type === "translation";

  let leadOverviewText = overviewText;
  let translationDescriptor = "";

  if (isTranslation && overviewText) {
    const paragraphs = overviewText
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (paragraphs.length > 1) {
      leadOverviewText = paragraphs[0];
      translationDescriptor = paragraphs.slice(1).join("\n\n");
    }
  }

  const facts = Array.isArray(data.facts)
    ? data.facts
    : Array.isArray((innerData as OverviewBlock)?.facts)
      ? (innerData as OverviewBlock).facts!
      : [];

  const sections = Array.isArray(data.sections)
    ? data.sections
    : Array.isArray((innerData as OverviewBlock)?.sections)
      ? (innerData as OverviewBlock).sections!
      : [];

  const sources = Array.isArray(data.sources) ? data.sources : [];
  const isCurrency = data.response_type === "currency" && !!data.currency;
  const hasRichGroundedSources =
    data.response_type === "currency" ||
    data.response_type === "market_data";

  let html = `
    <div class="ai-box ${isCurrency ? "ai-box-currency" : ""} ${isTranslation ? "ai-box-translation" : ""}" data-ai-overview-root>
      <div class="ai-overview-header-row">
        <h2 class="ai-title">AI Overview</h2>
        <button
          type="button"
          class="ai-overview-collapse-close"
          data-ai-overview-close
          aria-label="Collapse AI Overview"
          title="Collapse"
          hidden
        >×</button>
      </div>
      <div class="ai-overview-collapsible" data-ai-overview-content>
  `;

  if (overviewTitle || overviewText) {
    html += `<div class="ai-overview-block">`;
    if (overviewTitle) {
      html += `<h3 class="ai-overview-title">${escapeHtml(overviewTitle)}</h3>`;
    }
    if (leadOverviewText) {
      html += `<div class="ai-overview-text">${renderAIRichText(
        leadOverviewText,
        { variant: "overview" },
      )}</div>`;
    }
    html += `</div>`;
  }

  if (isTranslation && data.translation?.translation) {
    html += `
      <div
        class="ai-overview-dictionary-embed"
        data-ai-dictionary-embed
        aria-live="polite"
      ></div>
    `;
  }

  if (isTranslation && translationDescriptor) {
    html += `
      <section class="ai-translation-section ai-translation-description-section">
        <h3>Description</h3>
        <div class="ai-translation-section-content">
          ${renderAIRichText(translationDescriptor, { variant: "overview" })}
        </div>
      </section>
    `;
  }

  if (isCurrency) {
    html += `
      <div
        class="ai-overview-currency-embed"
        data-ai-currency-embed
        aria-live="polite"
      >
        <div class="ai-overview-currency-loading">Loading currency controls…</div>
      </div>
    `;
  }
if (facts.length > 0) {
    html += `
      <div class="ai-facts-container">
        <ul class="ai-facts">
          ${facts.map((fact) => `<li>${renderAIRichText(String(fact), { variant: "overview" })}</li>`).join("")}
        </ul>
      </div>
    `;
  }

  if (sections.length > 0) {
    if (isTranslation) {
      html += `<div class="ai-translation-sections">`;
      sections.forEach((section) => {
        html += `
          <section class="ai-translation-section">
            <h3>${escapeHtml(section.title || "More Information")}</h3>
            <div class="ai-translation-section-content">
              ${renderAIRichText(section.content || "", { variant: "overview" })}
            </div>
          </section>
        `;
      });
      html += `</div>`;
    } else {
      html += `<div class="ai-sections">`;
      sections.forEach((section, index) => {
        html += `
          <div class="ai-section" data-index="${index}">
            <div class="ai-section-header">
              <span class="toggle-icon">▶</span>
              ${escapeHtml(section.title || "More Information")}
            </div>
            <div class="ai-section-content" style="display: none;">
              ${renderAIRichText(section.content || "", { variant: "overview" })}
            </div>
          </div>
        `;
      });
      html += `</div>`;
    }
  }

  if (sources.length > 0) {
    html += hasRichGroundedSources
      ? renderRichGroundedSources(sources)
      : `
        <div class="ai-sources">
          <hr class="ai-divider">
          <strong>Sources:</strong>
          <ul>
            ${sources.map(renderSource).join("")}
          </ul>
        </div>
      `;
  }

  html += `
      </div>
      <button
        type="button"
        class="ai-overview-show-more"
        data-ai-overview-show-more
        aria-expanded="false"
      >
        Show more
      </button>
    </div>
  `;
  container.innerHTML = html;

  setupExpandableSections(container);

  // Translation AI Overview embeds the existing DictionaryCard using the
  // translated TARGET word. This prevents a Yoruba request from rendering the
  // English source entry ("please") instead of the Yoruba entry ("jọ̀wọ́").
  if (isTranslation && data.translation?.translation) {
    const dictionaryHost = container.querySelector<HTMLElement>(
      "[data-ai-dictionary-embed]",
    );
    if (dictionaryHost) {
      void renderDictionaryCard(
        dictionaryHost,
        String(data.translation.translation),
      ).finally(() => {
        window.requestAnimationFrame(() => {
          window.dispatchEvent(new Event("resize"));
        });
      });
    }
  }

  // Embed the exact same existing currency components using the already
  // grounded backend payload. Initial display causes no second resolve call.
  if (isCurrency && data.currency) {
    const host = container.querySelector<HTMLElement>("[data-ai-currency-embed]");
    if (host) {
      const mount =
        data.currency.mode === "today"
          ? mountTodaysCurrencyData(host, data.currency)
          : mountCurrencyConverterData(host, data.currency);

      void mount.finally(() => {
        // Re-measure collapse after async dropdown/currency component mount.
        window.requestAnimationFrame(() => {
          window.dispatchEvent(new Event("resize"));
        });
      });
    }
  }
setupOverviewCollapse(container, {
    query: options.query || "",
    overviewText,
    responseType: data.response_type || "",
    sources,
    currency: data.currency,
    translation: data.translation,
  });
}

function faviconForSource(source: AiSource): string {
  const raw = source.url || "";
  if (!raw) return "";

  try {
    const url = new URL(raw);
    return `${url.origin}/favicon.ico`;
  } catch {
    return "";
  }
}

function sourceDescription(source: AiSource): string {
  const label = (source.title || source.domain || "").toLowerCase();
  if (label.includes("ngn market")) {
    return "Nigerian market and foreign-exchange data";
  }
  if (label.includes("exchangerate")) {
    return "Global exchange-rate data";
  }
  return source.snippet || source.domain || "Exchange-rate source";
}

function renderRichGroundedSources(
  sources: Array<AiSource | string>,
): string {
  return `
    <div class="ai-sources ai-currency-sources">
      <hr class="ai-divider">
      <strong>Sources</strong>
      <div class="ai-currency-source-grid">
        ${sources.map((source) => {
          if (typeof source === "string") {
            return `
              <div class="ai-currency-source-card">
                <span class="ai-currency-source-icon ai-currency-source-letter">
                  ${escapeHtml(source.slice(0, 1).toUpperCase())}
                </span>
                <span class="ai-currency-source-copy">
                  <strong>${escapeHtml(source)}</strong>
                  <span>Exchange-rate source</span>
                </span>
              </div>
            `;
          }

          const label = source.title || source.domain || source.url || "Source";
          const favicon = faviconForSource(source);
          const description = sourceDescription(source);
          const content = `
            <span class="ai-currency-source-icon">
              ${
                favicon
                  ? `<img src="${escapeHtml(favicon)}" alt="" loading="lazy" onerror="this.remove();this.parentElement?.classList.add('ai-currency-source-letter');this.parentElement.textContent='${escapeHtml(label.slice(0, 1).toUpperCase())}'">`
                  : escapeHtml(label.slice(0, 1).toUpperCase())
              }
            </span>
            <span class="ai-currency-source-copy">
              <strong>${escapeHtml(label)}</strong>
              <span>${escapeHtml(description)}</span>
            </span>
            ${source.url ? `<span class="ai-currency-source-arrow" aria-hidden="true">↗</span>` : ""}
          `;

          return source.url
            ? `<a class="ai-currency-source-card" href="${escapeHtml(source.url)}" target="_blank" rel="noopener noreferrer">${content}</a>`
            : `<div class="ai-currency-source-card">${content}</div>`;
        }).join("")}
      </div>
    </div>
  `;
}

function renderSource(source: AiSource | string): string {
  if (typeof source === "string") {
    return `<li>${escapeHtml(source)}</li>`;
  }

  const label = source.title || source.domain || source.url || "Source";
  if (!source.url) {
    return `<li>${escapeHtml(label)}</li>`;
  }

  return `
    <li>
      <a
        href="${escapeHtml(source.url)}"
        target="_blank"
        rel="noopener noreferrer"
      >${escapeHtml(label)}</a>
    </li>
  `;
}

function setupExpandableSections(container: HTMLElement) {
  const headers = container.querySelectorAll(".ai-section-header");

  headers.forEach((header) => {
    header.addEventListener("click", () => {
      const content = header.nextElementSibling as HTMLElement;
      const icon = header.querySelector(".toggle-icon") as HTMLElement;

      if (content.style.display === "none" || !content.style.display) {
        content.style.display = "block";
        icon.textContent = "▼";
      } else {
        content.style.display = "none";
        icon.textContent = "▶";
      }
    });
  });
}


function setupOverviewCollapse(
  container: HTMLElement,
  seed: {
    query: string;
    overviewText: string;
    responseType?: string;
    sources?: Array<AiSource | string>;
    currency?: CurrencyResult;
    translation?: TranslationOverviewPayload;
  },
) {
  const root = container.querySelector<HTMLElement>("[data-ai-overview-root]");
  const content = container.querySelector<HTMLElement>("[data-ai-overview-content]");
  const showMore = container.querySelector<HTMLButtonElement>(
    "[data-ai-overview-show-more]",
  );
  const close = container.querySelector<HTMLButtonElement>(
    "[data-ai-overview-close]",
  );

  if (!root || !content || !showMore || !close) return;

  const contentNeedsCollapse = () => {
    const wasExpanded = root.classList.contains("is-expanded");
    root.classList.add("is-measuring");
    root.classList.remove("is-expanded");

    const needsCollapse = content.scrollHeight > content.clientHeight + 8;

    root.classList.remove("is-measuring");
    if (wasExpanded) root.classList.add("is-expanded");

    return needsCollapse;
  };

  const applyCollapsedState = () => {
    root.classList.remove("is-expanded");
    const shouldCollapse = contentNeedsCollapse();
    root.classList.toggle("is-collapsible", shouldCollapse);
    showMore.hidden = !shouldCollapse;
    close.hidden = true;
    showMore.setAttribute("aria-expanded", "false");
  };

  showMore.addEventListener("click", () => {
    if (!root.classList.contains("is-collapsible")) return;

    if (window.matchMedia("(max-width: 768px)").matches) {
      openAIZoneExtended({
        query: seed.query,
        answer: seed.overviewText,
        responseType: seed.responseType,
        sources: (seed.sources || []).filter(
          (source): source is AiSource => typeof source !== "string",
        ),
        currency: seed.currency,
        translation: seed.translation,
        showCurrencyComponent:
          seed.responseType === "currency" && !!seed.currency,
        onClose: () => {
          applyCollapsedState();
        },
      });
      return;
    }

    root.classList.add("is-expanded");
    showMore.hidden = true;
    close.hidden = false;
    showMore.setAttribute("aria-expanded", "true");
  });

  close.addEventListener("click", () => {
    root.classList.remove("is-expanded");
    showMore.hidden = false;
    close.hidden = true;
    showMore.setAttribute("aria-expanded", "false");

    root.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  });

  applyCollapsedState();

  const resizeObserver =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
          if (!root.classList.contains("is-expanded")) {
            applyCollapsedState();
          }
        })
      : null;

  resizeObserver?.observe(content);

  window.addEventListener("resize", applyCollapsedState, {
    passive: true,
  });
}

function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
